/**
 * HeroImagePanel — a pair of admin controls dedicated to the Hero images
 * (center mascot, side image, and AI-generated title image):
 *   - HeroImageUploader: file/URL input + preview + upload endpoint call
 *   - HeroImageControls: sliders + color pickers (position, size, glow, z-index)
 * Both persist to cfg.hero.<prefix>_*  (or cfg.hero.<field>).
 */
import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { setPath } from "./setPath.js";

export function HeroImageUploader({ label, field, cfg, setCfg, testid, hint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const value = cfg.hero?.[field] || "";
  const fullUrl = value.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Apenas ficheiros de imagem."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 8 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/homepage-config/upload-image?kind=hero_${field}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (!url) throw new Error("sem URL");
      setCfg(setPath(cfg, ["hero", field], url));
      toast.success(`Imagem carregada (${(res.data.bytes/1024).toFixed(0)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload falhou");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`${testid}-wrap`}>
      <Label className="text-sm font-bold text-slate-700">{label}</Label>
      {hint && <p className="text-[11px] text-slate-500 mb-2">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`${testid}-file`}
      />
      <div className="flex flex-col sm:flex-row gap-2 mt-1">
        <Button variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="rounded-lg flex-shrink-0" data-testid={`${testid}-upload-btn`}>
          <Upload className="w-4 h-4 mr-1.5" /> {busy ? "A carregar…" : "Carregar imagem"}
        </Button>
        <Input
          value={value}
          onChange={(e) => setCfg(setPath(cfg, ["hero", field], e.target.value))}
          placeholder="/api/static/... ou URL externo"
          data-testid={`${testid}-url`}
        />
        {value && (
          <Button variant="outline" onClick={() => setCfg(setPath(cfg, ["hero", field], ""))} className="rounded-lg flex-shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`${testid}-clear`}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      {fullUrl && (
        <div
          className="mt-2 rounded-lg overflow-hidden border border-slate-200 grid place-items-center"
          style={{
            backgroundImage: "linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
            backgroundColor: "#f8fafc",
          }}
          data-testid={`${testid}-preview`}
        >
          <img src={fullUrl} alt="preview" className="max-h-48 object-contain p-2" />
        </div>
      )}
    </div>
  );
}

export function HeroImageControls({ prefix, cfg, setCfg, defaults, testid }) {
  const h = cfg.hero || {};
  const get = (k, d) => (h[`${prefix}_${k}`] ?? d);
  const set = (k, v) => setCfg(setPath(cfg, ["hero", `${prefix}_${k}`], v));
  const setMany = (obj) => {
    let next = cfg;
    Object.entries(obj).forEach(([k, v]) => { next = setPath(next, ["hero", `${prefix}_${k}`], v); });
    setCfg(next);
  };
  const row = "grid grid-cols-1 sm:grid-cols-2 gap-3";
  const num = (v) => (v === "" || v == null ? 0 : Number(v));

  const PRESETS = prefix === "center_image"
    ? {
        small:   { label: "Pequena",   values: { width: 360, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 35 } },
        medium:  { label: "Média",     values: { width: 480, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 45 } },
        large:   { label: "Grande",    values: { width: 620, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 55 } },
        huge:    { label: "Enorme",    values: { width: 780, scale: 1.05, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 65 } },
      }
    : prefix === "title_image"
    ? {
        small:   { label: "Pequeno",   values: { width: 380, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 35 } },
        medium:  { label: "Médio",     values: { width: 480, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 45 } },
        large:   { label: "Grande",    values: { width: 620, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 55 } },
        wide:    { label: "Cartaz",    values: { width: 780, scale: 1.05, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 70 } },
      }
    : {
        small:   { label: "Pequena",   values: { width: 460, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 40 } },
        medium:  { label: "Média",     values: { width: 620, scale: 1.0, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 55 } },
        large:   { label: "Grande",    values: { width: 780, scale: 1.05, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 65 } },
        huge:    { label: "Enorme",    values: { width: 900, scale: 1.1, offset_x: 0, offset_y: 0, rotate: 0, glow_intensity: 75 } },
      };

  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 space-y-3" data-testid={`${testid}-controls`}>
      <p className="text-[11px] font-bold text-indigo-800 uppercase tracking-wide">Posição · Dimensão · Efeitos{prefix === "side_video" ? " — do vídeo lateral" : ""}</p>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-2 space-y-1.5">
        <p className="text-[11px] font-black text-amber-900 uppercase tracking-wide">⚡ Presets rápidos</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PRESETS).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMany(p.values)}
              className="px-3 py-1.5 rounded-md text-[11px] font-bold bg-amber-500 hover:bg-amber-600 text-white transition shadow-sm"
              data-testid={`${testid}-preset-${key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className={row}>
        <div>
          <Label className="text-xs">Largura (px)</Label>
          <Input type="number" min={80} max={1200} value={get("width", defaults.width)} onChange={(e) => set("width", num(e.target.value))} data-testid={`${testid}-width`} />
        </div>
        <div>
          <Label className="text-xs">Escala ({get("scale", 1).toFixed(2)}×)</Label>
          <Input type="range" min={0.4} max={2} step={0.05} value={get("scale", 1)} onChange={(e) => set("scale", Number(e.target.value))} data-testid={`${testid}-scale`} />
        </div>
      </div>
      <div className={row}>
        <div>
          <Label className="text-xs">Offset X ({get("offset_x", 0)}px)</Label>
          <Input type="range" min={-800} max={800} step={1} value={get("offset_x", 0)} onChange={(e) => set("offset_x", Number(e.target.value))} data-testid={`${testid}-offset-x`} />
        </div>
        <div>
          <Label className="text-xs">Offset Y ({get("offset_y", 0)}px)</Label>
          <Input type="range" min={-800} max={800} step={1} value={get("offset_y", 0)} onChange={(e) => set("offset_y", Number(e.target.value))} data-testid={`${testid}-offset-y`} />
        </div>
      </div>
      <div className={row}>
        <div>
          <Label className="text-xs">Rotação ({get("rotate", 0)}°)</Label>
          <Input type="range" min={-45} max={45} step={1} value={get("rotate", 0)} onChange={(e) => set("rotate", Number(e.target.value))} data-testid={`${testid}-rotate`} />
        </div>
        <div>
          <Label className="text-xs">Intensidade do Glow ({get("glow_intensity", defaults.glow_intensity)}%)</Label>
          <Input type="range" min={0} max={100} step={1} value={get("glow_intensity", defaults.glow_intensity)} onChange={(e) => set("glow_intensity", Number(e.target.value))} data-testid={`${testid}-glow-intensity`} />
        </div>
      </div>
      <div className={row}>
        <div>
          <Label className="text-xs">Opacidade ({get("opacity", 100)}%)</Label>
          <Input type="range" min={0} max={100} step={1} value={get("opacity", 100)} onChange={(e) => set("opacity", Number(e.target.value))} data-testid={`${testid}-opacity`} />
        </div>
        <div />
      </div>
      <div className={row}>
        <div>
          <Label className="text-xs">Cor do Glow</Label>
          <div className="flex items-center gap-2">
            <input type="color" value={get("glow_color", defaults.glow_color)} onChange={(e) => set("glow_color", e.target.value)} className="w-10 h-9 rounded border" data-testid={`${testid}-glow-color`} />
            <Input value={get("glow_color", defaults.glow_color)} onChange={(e) => set("glow_color", e.target.value)} className="font-mono text-xs" />
          </div>
        </div>
        {prefix === "center_image" && (
          <div>
            <Label className="text-xs">Tamanho do subtítulo (px)</Label>
            <Input type="number" min={10} max={40} value={get("subtitle_size", 18)} onChange={(e) => set("subtitle_size", num(e.target.value))} data-testid={`${testid}-subtitle-size`} />
          </div>
        )}
      </div>
      <div>
        <Label className="text-xs">Camada / Ordem Z (maior = à frente)</Label>
        <Input type="number" min={0} max={100} value={get("z", prefix === "center_image" ? 10 : (prefix === "side_image" || prefix === "side_video" ? 40 : 5))} onChange={(e) => set("z", num(e.target.value))} data-testid={`${testid}-z`} />
        <p className="text-[10px] text-slate-500 mt-1">Ex: Mascote com Z=10 e Rapariga com Z=5 → mascote fica à frente. Inverte para ficar atrás. <b>Imagem lateral: usa ≥40 para sobrepor o cabeçalho (que é 30).</b></p>
      </div>
      {(prefix === "side_image" || prefix === "side_video") && (
        <div className="rounded-md border-2 border-fuchsia-300 bg-fuchsia-50 p-2 space-y-1.5">
          <Label className="text-xs font-black text-fuchsia-800 uppercase tracking-wide">✨ Efeito visual (aplica sobre {prefix === "side_video" ? "o vídeo" : "a menina"})</Label>
          <select
            value={get("effect", "glow")}
            onChange={(e) => set("effect", e.target.value)}
            className="w-full h-8 rounded border border-fuchsia-300 bg-white px-2 text-xs"
            data-testid={`${testid}-effect`}
          >
            <option value="none">Nenhum (só imagem)</option>
            <option value="glow">Glow (halo estático) — usa Cor + Intensidade acima</option>
            <option value="pulse_glow">Pulse Glow (halo respira)</option>
            <option value="rainbow_glow">Rainbow Glow (hue-shift arco-íris)</option>
            <option value="breathe">Breathe (leve zoom in/out)</option>
            <option value="float">Float (flutua para cima e baixo)</option>
            <option value="shake">Shake (tremer subtilmente)</option>
            <option value="wiggle">Wiggle (rotação a bater ±2°)</option>
            <option value="tilt_sway">Tilt Sway (pêndulo lento ±4°)</option>
            <option value="spin_slow">Spin Slow (rotação 360° em 24s)</option>
            <option value="hologram">Hologram (hue-shift + brilho)</option>
            <option value="neon_outline">Neon Flicker (glow pisca)</option>
            <option value="vhs_glitch">VHS Glitch (jitter + hue)</option>
          </select>
          <p className="text-[10px] text-fuchsia-800/80">
            Efeitos que envolvem &quot;glow&quot; herdam a <b>Cor do Glow</b> e a <b>Intensidade do Glow</b> acima. A intensidade agora escala o raio do halo — a 100% chega a ~200px de auréola.
          </p>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          Object.entries(defaults).forEach(([k, v]) => set(k, v));
          set("offset_x", 0); set("offset_y", 0); set("scale", 1); set("rotate", 0);
        }}
        className="text-[11px] font-bold text-indigo-700 hover:text-indigo-900 underline"
        data-testid={`${testid}-reset`}
      >
        Repor valores padrão
      </button>
    </div>
  );
}
